import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ShieldCheck, UserX, UserCheck, Trash2, Pencil, Eye, LogIn, Search, X, Mail,
  Music2, Users, ScrollText, ClipboardList, AlertTriangle, Check, Star, KeyRound,
  CheckCircle2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import OnboardingPanel from "../components/admin/OnboardingPanel";
import AnnouncementsPanel from "../components/admin/AnnouncementsPanel";

export default function Admin() {
  const { refresh: authRefresh, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [audit, setAudit] = useState([]);
  const [depBroadcasts, setDepBroadcasts] = useState([]);
  const [depStats, setDepStats] = useState(null);
  const [tab, setTab] = useState("users");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [pwTools, setPwTools] = useState(null);

  const refresh = async () => {
    try {
      const [u, s, b, a, db, ds] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/audit-log"),
        api.get("/admin/deps/broadcasts"),
        api.get("/admin/deps/stats"),
      ]);
      setUsers(u.data || []);
      setStats(s.data);
      setBookings(b.data || []);
      setAudit(a.data || []);
      setDepBroadcasts(db.data || []);
      setDepStats(ds.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filteredUsers = useMemo(() => {
    if (!q) return users;
    const needle = q.toLowerCase();
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(needle)
      || (u.email || "").toLowerCase().includes(needle)
      || (u.role || "").toLowerCase().includes(needle),
    );
  }, [users, q]);

  const toggleSuspend = async (u) => {
    await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
    toast.success(`${u.name} ${u.suspended ? "reinstated" : "suspended"}`);
    refresh();
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})?\n\nThis also wipes their profile, availability and contacts.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.name}?\n\nYour admin session will be replaced. Logout to return to your admin account.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      toast.success(`Now signed in as ${data.impersonating.name}`);
      await authRefresh();
      navigate("/home", { replace: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 pb-12" data-testid="admin-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline inline-flex items-center gap-2"><ShieldCheck size={14} /> Admin</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Operations</h1>
          <p className="text-ink-600 text-sm mt-1">Stats, accounts, support, and audit trail.</p>
        </div>
        <Link to="/profile#password" className="btn-secondary text-sm" data-testid="admin-change-password-link">
          <KeyRound size={14} /> Change my password
        </Link>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats-grid">
          <StatCard icon={Users} label="Users" value={stats.users} sublabel={`+${stats.signups_last_24h || 0} today`} />
          <StatCard icon={Music2} label="Musicians" value={stats.musicians} sublabel={`${stats.bookers || 0} bookers · ${stats.conductors || 0} conductors`} />
          <StatCard icon={ClipboardList} label="Bookings" value={stats.bookings} sublabel={`${stats.confirmed || 0} confirmed · ${stats.pending || 0} pending`} />
          <StatCard icon={Star} label="Founding members" value={stats.founding_members || 0} sublabel={`${stats.gigs_open || 0} gigs open · ${stats.ensembles || 0} ensembles`} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-ivory-200 pb-1">
        {[
          { v: "users", label: "Users", count: users.length },
          { v: "bookings", label: "Bookings", count: bookings.length },
          { v: "deps", label: "Deps", count: depBroadcasts.length },
          { v: "onboarding", label: "Onboarding", count: null },
          { v: "announcements", label: "Announcements", count: null },
          { v: "audit", label: "Audit log", count: audit.length },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${tab === t.v ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}
            data-testid={`admin-tab-${t.v}`}
          >
            {t.label}{t.count !== null && <span className="opacity-70"> ({t.count})</span>}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          <div className="card-base p-3 flex gap-2 items-center">
            <Search size={16} className="text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, or role"
              className="input-field !py-1.5 flex-1"
              data-testid="admin-search-input"
            />
          </div>
          <ul className="space-y-2">
            {filteredUsers.map((u) => (
              <li key={u.id}>
                <article className="card-base p-4 flex flex-wrap items-center gap-3" data-testid={`admin-user-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold inline-flex items-center gap-2 flex-wrap">
                      {u.name}
                      <span className={`pill !text-[10px] ${u.role === "admin" ? "bg-terracotta-500 text-white" : "bg-ivory-100 text-ink-700"}`}>{u.role}</span>
                      {u.email_verified === true ? (
                        <span className="pill !text-[10px] bg-sage-100 text-sage-700" data-testid={`admin-verified-pill-${u.id}`}>verified</span>
                      ) : (
                        <span className="pill !text-[10px] bg-amber-soft text-amber-deep" data-testid={`admin-unverified-pill-${u.id}`}>unverified</span>
                      )}
                      {u.suspended && <span className="pill !text-[10px] bg-amber-soft text-amber-deep">suspended</span>}
                      {u.founding_member && <Star size={12} className="fill-terracotta-500 text-terracotta-500" title="Founding Member" />}
                    </div>
                    <div className="text-xs text-ink-500 truncate">{u.email}</div>
                    <div className="text-xs text-ink-400 mt-0.5">
                      {u.tier ? <span title="Billing tier">Tier: {u.tier}</span> : null}
                      {u.tier ? " · " : ""}joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setViewing(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" title="View activity" data-testid={`admin-view-${u.id}`}>
                      <Eye size={14} /> View
                    </button>
                    <button onClick={() => setEditing(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" title="Edit account" data-testid={`admin-edit-${u.id}`}>
                      <Pencil size={14} /> Edit
                    </button>
                    <button onClick={() => setPwTools(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" title="Password tools" data-testid={`admin-pw-tools-${u.id}`}>
                      <KeyRound size={14} />
                    </button>
                    {u.role !== "admin" && (
                      <button onClick={() => impersonate(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" title="View as user" data-testid={`admin-impersonate-${u.id}`}>
                        <LogIn size={14} /> View as
                      </button>
                    )}
                    <button onClick={() => toggleSuspend(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs" title={u.suspended ? "Reinstate" : "Suspend"} data-testid={`admin-suspend-${u.id}`}>
                      {u.suspended ? <UserCheck size={14} /> : <UserX size={14} />}
                    </button>
                    {u.id !== currentUser?.id && (
                      <button onClick={() => removeUser(u)} className="btn-secondary !py-1.5 !px-2.5 !text-xs text-terracotta-600" title="Delete account" data-testid={`admin-delete-${u.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </article>
              </li>
            ))}
          </ul>
        </>
      )}

      {tab === "bookings" && (
        <ul className="space-y-2" data-testid="admin-bookings-list">
          {bookings.length === 0 && <p className="text-ink-500 text-sm">No bookings yet.</p>}
          {bookings.map((b) => (
            <li key={b.id || b._id} className="card-base p-3 text-sm">
              <div className="font-medium">{b.title || b.event_type || "Booking"}</div>
              <div className="text-ink-500 text-xs">{b.date} · status: {b.status}</div>
            </li>
          ))}
        </ul>
      )}

      {tab === "deps" && (
        <div className="space-y-3" data-testid="admin-deps-tab">
          {depStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MiniStat label="Broadcasts" value={depStats.broadcasts.total} sub={`${depStats.broadcasts.this_week} in the last 7 days`} />
              <MiniStat label="Filled" value={depStats.broadcasts.filled} sub={`${Math.round(depStats.broadcasts.filled_rate * 100)}% rate`} />
              <MiniStat label="Responses" value={depStats.responses.total} sub={`${depStats.responses.yes} yes · ${depStats.responses.maybe} maybe · ${depStats.responses.no} no`} />
              <MiniStat label="Emails sent" value={depStats.emails.delivered_sent} sub={`${depStats.emails.delivered_failed} failed`} />
            </div>
          )}
          {depStats?.beta_monitoring && (
            <div className="card-base p-3 text-xs text-ink-600 flex flex-wrap gap-4" data-testid="admin-beta-monitoring">
              <span><strong className="text-ink-900">{depStats.beta_monitoring.failed_login_lockouts_active}</strong> active login lockouts</span>
              <span><strong className="text-ink-900">{depStats.beta_monitoring.expired_unused_reset_tokens}</strong> expired unused reset tokens</span>
            </div>
          )}
          <ul className="space-y-2" data-testid="admin-deps-list">
            {depBroadcasts.length === 0 && <p className="text-ink-500 text-sm">No dep broadcasts yet.</p>}
            {depBroadcasts.map((b) => (
              <li key={b.id}>
                <Link to={`/deps/${b.id}`} className="card-base p-3 text-sm block hover:-translate-y-[1px] transition" data-testid={`admin-dep-${b.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-medium">{b.instrument} · {b.event_type.replace("_", " ")} · {b.date}</div>
                    <span className={`pill text-xs ${b.status === "filled" ? "bg-sage-100 text-sage-700" : "bg-amber-100 text-amber-700"}`}>
                      {b.status}
                    </span>
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5 truncate">
                    {b.from_name} ({b.from_email}) · {b.sent}/{b.total} delivered
                    {b.venue && ` · ${b.venue}`}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "onboarding" && (
        <OnboardingPanel />
      )}

      {tab === "announcements" && <AnnouncementsPanel />}

      {tab === "audit" && (
        <ul className="space-y-2" data-testid="admin-audit-list">
          {audit.length === 0 && <p className="text-ink-500 text-sm">No admin actions yet.</p>}
          {audit.map((a, i) => (
            <li key={i} className="card-base p-3 text-sm flex items-start gap-3">
              <AuditIcon kind={a.kind} />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{prettyAudit(a)}</div>
                <div className="text-xs text-ink-500">
                  {a.admin_email} → {a.target_email || a.target_id}
                </div>
                <div className="text-xs text-ink-400">{new Date(a.at).toLocaleString()}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && <EditUserModal user={editing} onClose={() => { setEditing(null); refresh(); }} />}
      {viewing && <UserActivityModal user={viewing} onClose={() => setViewing(null)} />}
      {pwTools && <PasswordToolsModal user={pwTools} onClose={() => setPwTools(null)} />}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="card-base p-3">
      <div className="label-overline">{label}</div>
      <div className="font-heading text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


function StatCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="card-base p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={16} className="text-sage-600" />
      <div className="font-heading text-3xl font-bold tracking-tight mt-2">{value}</div>
      <div className="text-xs text-ink-600 mt-0.5">{label}</div>
      {sublabel && <div className="text-[10px] text-ink-400 mt-1">{sublabel}</div>}
    </div>
  );
}

function AuditIcon({ kind }) {
  const map = {
    admin_impersonate: <LogIn size={16} className="text-sage-600" />,
    admin_suspend: <UserX size={16} className="text-amber-deep" />,
    admin_reinstate: <UserCheck size={16} className="text-sage-600" />,
    admin_delete: <Trash2 size={16} className="text-terracotta-600" />,
  };
  return map[kind] || <ScrollText size={16} className="text-ink-500" />;
}

function prettyAudit(a) {
  switch (a.kind) {
    case "admin_impersonate": return "Signed in as user";
    case "admin_suspend": return "Suspended user";
    case "admin_reinstate": return "Reinstated user";
    case "admin_delete": return "Deleted user";
    case "announcement_sent": return `Sent announcement to ${a.detail?.recipients ?? "?"} people${a.detail?.email_dry_run ? " (email dry run)" : ""}`;
    default: return a.kind;
  }
}


// ---------- Edit user modal ----------

function PasswordToolsModal({ user, onClose }) {
  const [busy, setBusy] = useState(null);   // which action is running
  const [tempPw, setTempPw] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [showTempPw, setShowTempPw] = useState(false);

  const run = async (action, fn, successMsg) => {
    setBusy(action);
    try {
      await fn();
      toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  const sendReset = () => run("reset",
    () => api.post(`/admin/users/${user.id}/send-password-reset`),
    "Reset email sent");
  const unlock = () => run("unlock",
    () => api.post(`/admin/users/${user.id}/unlock`),
    "Lockout cleared");
  const force = () => run("force",
    () => api.post(`/admin/users/${user.id}/force-password-change`),
    "User will be prompted to change password");
  const markVerified = () => run("verify",
    () => api.post(`/admin/users/${user.id}/mark-email-verified`),
    "Email marked verified");
  const setTemp = () => {
    if (tempPw.length < 6) { toast.error("Temp password must be at least 6 characters."); return; }
    run("set",
      () => api.post(`/admin/users/${user.id}/set-password`, { new_password: tempPw, force_change: forceChange }),
      "Temporary password set");
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="admin-pw-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><KeyRound size={18} /> Password tools</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="admin-pw-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-ink-600 mb-5">For <strong>{user.name}</strong> · <span className="text-ink-500">{user.email}</span></p>

        <div className="space-y-3">
          <button onClick={sendReset} disabled={!!busy} className="btn-secondary w-full justify-center" data-testid="admin-pw-send-reset">
            <Mail size={14} /> {busy === "reset" ? "Sending…" : "Email reset link"}
          </button>

          <button onClick={force} disabled={!!busy} className="btn-secondary w-full justify-center" data-testid="admin-pw-force">
            <AlertTriangle size={14} /> {busy === "force" ? "Updating…" : "Force change on next login"}
          </button>

          <button onClick={unlock} disabled={!!busy} className="btn-secondary w-full justify-center" data-testid="admin-pw-unlock">
            <ShieldCheck size={14} /> {busy === "unlock" ? "Unlocking…" : "Unlock account (clear failed logins)"}
          </button>

          <button onClick={markVerified} disabled={!!busy} className="btn-secondary w-full justify-center" data-testid="admin-mark-email-verified">
            <CheckCircle2 size={14} /> {busy === "verify" ? "Marking…" : "Mark email verified"}
          </button>

          <div className="border-t border-ivory-200 pt-4 mt-2">
            <div className="label-overline mb-2">Set temporary password</div>
            <div className="flex gap-2">
              <input
                type={showTempPw ? "text" : "password"}
                placeholder="Min 6 characters"
                className="input-field flex-1"
                value={tempPw}
                onChange={(e) => setTempPw(e.target.value)}
                data-testid="admin-pw-temp-input"
              />
              <button type="button" onClick={() => setShowTempPw((v) => !v)}
                className="btn-ghost !px-3" data-testid="admin-pw-temp-toggle">
                {showTempPw ? "Hide" : "Show"}
              </button>
            </div>
            <label className="inline-flex items-center gap-2 text-sm mt-3 cursor-pointer">
              <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)}
                className="w-4 h-4 accent-sage-500" data-testid="admin-pw-force-checkbox" />
              Require user to change it on next login
            </label>
            <button onClick={setTemp} disabled={!!busy || tempPw.length < 6}
              className="btn-primary w-full justify-center mt-3" data-testid="admin-pw-set-temp">
              <KeyRound size={14} /> {busy === "set" ? "Setting…" : "Set temporary password"}
            </button>
            <p className="text-xs text-ink-500 mt-2">
              The hashed password is stored — never visible to you afterwards. Tell the user verbally or via a separate channel.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}


function EditUserModal({ user, onClose }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", role: user.role || "musician" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      if (form.name !== user.name) payload.name = form.name;
      if (form.email !== user.email) payload.email = form.email;
      if (form.role !== user.role) payload.role = form.role;
      if (Object.keys(payload).length === 0) {
        toast.info("No changes");
        onClose();
        return;
      }
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success("Account updated");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="admin-edit-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Edit account</h3>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-overline block mb-2">Name</label>
            <input className="input-field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-edit-name" />
          </div>
          <div>
            <label className="label-overline block mb-2">Email</label>
            <input className="input-field" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-edit-email" />
          </div>
          <div>
            <label className="label-overline block mb-2">Role</label>
            <select className="input-field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-edit-role">
              <option value="musician">musician</option>
              <option value="booker">booker</option>
              <option value="conductor">conductor</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-edit-submit">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}


// ---------- User activity modal ----------

class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[admin] modal render error", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="text-terracotta-600 text-sm space-y-2" data-testid="admin-activity-boundary-error">
          <p className="font-medium">Couldn't render activity:</p>
          <pre className="whitespace-pre-wrap break-all text-xs bg-ivory-50 p-2 rounded">{String(this.state.error?.message || this.state.error)}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function UserActivityModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) { setErr("No user selected."); return; }
    // eslint-disable-next-line no-console
    console.log("[admin] loading activity for", user.id);
    api.get(`/admin/users/${user.id}/activity`)
      .then(({ data }) => {
        // eslint-disable-next-line no-console
        console.log("[admin] activity loaded", data);
        setData(data);
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "Couldn't load activity";
        setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
        // eslint-disable-next-line no-console
        console.error("[admin] user activity load failed", e);
      });
  }, [user?.id]);

  const safeArr = (x) => (Array.isArray(x) ? x.filter(Boolean) : []);
  const ens = safeArr(data?.ensembles);
  const asMusician = safeArr(data?.bookings_as_musician);
  const asBooker = safeArr(data?.bookings_as_booker);
  const u = (data && typeof data.user === "object" && data.user) || {};
  const p = (data && typeof data.profile === "object" && data.profile) || null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="admin-activity-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl sm:rounded-3xl p-6 shadow-card max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-overline">User activity</div>
            <h3 className="font-heading text-xl font-semibold mt-1">{user?.name || "—"}</h3>
            <div className="text-xs text-ink-500">{user?.email || ""}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="admin-activity-close"><X size={18} /></button>
        </div>
        <ModalErrorBoundary>
          {err ? (
            <p className="text-terracotta-600 text-sm" data-testid="admin-activity-error">Couldn't load activity: {err}</p>
          ) : !data ? (
            <p className="text-ink-500 text-sm">Loading…</p>
          ) : (
            <div className="space-y-5 text-sm">
              <section>
                <h4 className="font-medium mb-1">Account</h4>
                <Row k="ID" v={u.id} />
                <Row k="Role" v={u.role} />
                <Row k="Tier" v={u.tier || "free"} />
                <Row k="Suspended" v={u.suspended ? "yes" : "no"} />
                <Row k="Email verified" v={u.email_verified === false ? "no — gated" : (u.email_verified_at ? `yes · ${u.email_verified_at} · ${u.email_verified_via || "link"}` : "yes")} />
                <Row k="Founding Member" v={u.founding_member ? "yes" : "no"} />
                <Row k="Referral code" v={u.referral_code || "—"} />
                <Row k="Created" v={u.created_at} />
              </section>
              {p && (
                <section>
                  <h4 className="font-medium mb-1">Profile</h4>
                  <Row k="Instrument" v={p.instrument} />
                  <Row k="Location" v={p.location} />
                  <Row k="Travel radius" v={p.travel_radius_km ? `${p.travel_radius_km} km` : "—"} />
                </section>
              )}
              <section>
                <h4 className="font-medium mb-1">Usage</h4>
                <Row k="Availability records" v={data.availability_count ?? 0} />
                <Row k="Saved contacts" v={data.contact_count ?? 0} />
                <Row k="Ensemble memberships" v={ens.length} />
              </section>
              {ens.length > 0 && (
                <section>
                  <h4 className="font-medium mb-1">Ensembles</h4>
                  <ul className="text-ink-700">
                    {ens.map((e, i) => <li key={e?.id || e?.name || i}>· {e?.name || "—"}</li>)}
                  </ul>
                </section>
              )}
              <section>
                <h4 className="font-medium mb-1">Bookings as musician ({asMusician.length})</h4>
                {asMusician.length === 0 ? (
                  <p className="text-ink-500">No bookings.</p>
                ) : (
                  <ul className="space-y-1">
                    {asMusician.slice(0, 8).map((b, i) => (
                      <li key={i} className="text-ink-700 text-xs">· {b?.date || "—"} · {b?.event_type || "event"} · <span className={`pill !text-[10px] ${b?.status === "confirmed" ? "bg-sage-100 text-sage-700" : "bg-ivory-100"}`}>{b?.status || "—"}</span></li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <h4 className="font-medium mb-1">Bookings as booker ({asBooker.length})</h4>
                {asBooker.length === 0 ? (
                  <p className="text-ink-500">None.</p>
                ) : (
                  <ul className="space-y-1">
                    {asBooker.slice(0, 8).map((b, i) => (
                      <li key={i} className="text-ink-700 text-xs">· {b?.date || "—"} · {b?.event_type || "event"} · <span className={`pill !text-[10px] ${b?.status === "confirmed" ? "bg-sage-100 text-sage-700" : "bg-ivory-100"}`}>{b?.status || "—"}</span></li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </ModalErrorBoundary>
      </div>
    </div>,
    document.body
  );
}

function Row({ k, v }) {
  // Convert anything non-stringable (objects, arrays) to JSON so a stray
  // unexpected value never crashes the modal.
  const display = v == null || v === ""
    ? "—"
    : (typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <div className="flex gap-2 py-0.5">
      <span className="text-ink-500 w-32 shrink-0">{k}</span>
      <span className="text-ink-800 break-all">{display}</span>
    </div>
  );
}
